/**
 * MfaVerification
 *
 * Enterprise MFA verification component with progressive disclosure.
 *
 * Single-method users see a clean code input (planner-web UX).
 * Multi-method users see a "Try another way" link that reveals a
 * card-style method selector (palm-web UX). SMS/Email methods show
 * a send-first step with resend countdown. Backup codes are behind
 * a separate "Use a backup code" link.
 *
 * Auto-submits on 6-digit completion (Stripe pattern) with a 300ms
 * debounce. All strings are translatable via an optional `t()` prop.
 *
 * @module @puffin-core/mfa-ui/MfaVerification
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { InlineFeedback } from '@puffin-core/page-shell/inline-feedback';
import { useMfaTranslate } from './hooks/useMfaTranslate.js';
import { MfaCodeInput } from './MfaCodeInput.jsx';
import { MfaMethodList } from './internal/MfaMethodList.jsx';
import { MfaOtpSendStep } from './internal/MfaOtpSendStep.jsx';
import { MfaPasskeyStep } from './internal/MfaPasskeyStep.jsx';
import { MfaBackupEntry } from './MfaBackupEntry.jsx';
import { PASSKEY_METHOD } from './internal/passkeyMethod.js';

/** Methods that require an OTP send step before code entry. */
const OTP_METHODS = new Set(['sms', 'email']);

/**
 * The methods this challenge OFFERS: a passkey only where the host can run its ceremony (`onVerifyPasskey`). A host
 * without one — a code-only surface — never shows a passkey choice it cannot honour. Module-private.
 *
 * @param {string[]} methods
 * @param {Function|undefined} onVerifyPasskey
 * @returns {string[]}
 */
function offerableMethods(methods, onVerifyPasskey) {
  return onVerifyPasskey ? methods : methods.filter((method) => method !== PASSKEY_METHOD);
}

/** Views within the progressive disclosure flow. */
const VIEW = {
  CODE_ENTRY: 'code_entry',
  METHOD_LIST: 'method_list',
  BACKUP_CODE: 'backup_code',
};

/**
 * Owns the flow's view state: which method is active, which view is showing,
 * and the code buffer. Module-private.
 *
 * SECURITY: every navigation handler CLEARS the code. That is load-bearing —
 * without it, digits typed for one method (or for the TOTP view) could be
 * carried into a verification against a DIFFERENT method or into the backup-code
 * view. Each `setCode('')` below is preserved exactly as it was.
 */
function useMfaViewState(initialMethod) {
  const [activeMethod, setActiveMethod] = useState(initialMethod);
  const [view, setView] = useState(VIEW.CODE_ENTRY);
  const [code, setCode] = useState('');

  const handleSelectMethod = useCallback((method) => {
    setActiveMethod(method);
    setCode('');
    setView(VIEW.CODE_ENTRY);
  }, []);

  const handleShowMethods = useCallback(() => {
    setView(VIEW.METHOD_LIST);
    setCode('');
  }, []);

  const handleShowBackup = useCallback(() => {
    setView(VIEW.BACKUP_CODE);
    setCode('');
  }, []);

  const handleBackFromMethods = useCallback(() => {
    setView(VIEW.CODE_ENTRY);
    setCode('');
  }, []);

  return {
    activeMethod,
    view,
    code,
    setCode,
    handleSelectMethod,
    handleShowMethods,
    handleShowBackup,
    handleBackFromMethods,
  };
}

/**
 * MFA-LOOP-001: clear the entered code on a rejected attempt so the user retypes
 * a fresh code (clear & refocus UX; the input components refocus on the clear).
 * A failed attempt is signalled by EITHER the error appearing (falsy → truthy)
 * OR the isLoading edge (true → false) while an error is present — the latter
 * covers repeated failures whose error message is unchanged. Seeding the refs
 * with the current props means mounting with a pre-existing error is not treated
 * as a fresh failure.
 *
 * Module-private. Moved WHOLE — the two refs, the seeding, the two-signal
 * condition and the write-back are byte-for-byte what they were.
 */
function useMfaCodeResetOnFailure({ error, isLoading, setCode }) {
  const prevErrorRef = useRef(error);
  const prevLoadingRef = useRef(isLoading);

  useEffect(() => {
    const errorAppeared = Boolean(error) && !prevErrorRef.current;
    const attemptFailed = Boolean(error) && prevLoadingRef.current && !isLoading;
    if (errorAppeared || attemptFailed) {
      setCode('');
    }
    prevErrorRef.current = error;
    prevLoadingRef.current = isLoading;
  }, [error, isLoading, setCode]);
}

/**
 * MFA-LOGIN-CONSOLIDATION-001: a TERMINAL error (challenge invalid / locked /
 * session expired — duck-typed `error.terminal`, set by login-shell's AuthError)
 * gets the canonical warning tone — polite, `role="status"` (R-SF-4) — + an explicit
 * return-to-sign-in affordance (wired to onCancel); retryable errors keep the error tone
 * (`role="alert"`) and the user stays on the step. The ONE feedback line (SAVE-FEEDBACK-CANON-001
 * P3 / R-SF-6). Shared by the code-entry + backup views.
 *
 * ONE error path for every method (LIFE-SHORTLISTED-001 D12b): the code-entry view renders this
 * alert for TOTP, SMS and email alike. It rendered only for a non-OTP method, while the code field
 * was still described by `errorId`, so a refused SMS or email code showed no reason at all.
 *
 * Module-private. The `errorMessage ? (…) : null` ternary this replaces was
 * already null-safe, so the early `return null` renders identically.
 */
function MfaErrorAlert({ errorMessage, errorId, isTerminal, onCancel, t }) {
  if (!errorMessage) {
    return null;
  }

  return (
    <>
      <InlineFeedback
        tone={isTerminal ? 'warning' : 'error'}
        id={errorId}
        testId="mfa-error"
        message={errorMessage}
      />
      {isTerminal && (
        <div className="mfa-alt-actions">
          <button
            type="button"
            className="mfa-alt-link"
            onClick={onCancel}
            data-testid="mfa-return-signin"
          >
            {t('link_return_to_signin')}
          </button>
        </div>
      )}
    </>
  );
}

/**
 * LOGIN-CONVERGENCE: the view of a challenge offering NOTHING this host can answer. A current server refuses an unservable
 * sign-in (`MFA_NO_USABLE_METHOD`) before any challenge; an OLDER one (a deploy window) sends a challenge offering
 * nothing. The person reads the same words, a terminal refusal, and returns to sign-in: never a code field no method
 * could answer. Module-private.
 */
function MfaNoUsableMethod({ className, onCancel, t }) {
  return (
    <div className={className} data-testid="mfa-verification">
      <MfaErrorAlert
        errorMessage={t('error_no_usable_method')}
        errorId="mfa-error-message"
        isTerminal
        onCancel={onCancel}
        t={t}
      />
    </div>
  );
}

MfaNoUsableMethod.propTypes = {
  className: PropTypes.string.isRequired,
  onCancel: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

MfaErrorAlert.propTypes = {
  errorMessage: PropTypes.string,
  errorId: PropTypes.string,
  isTerminal: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

/** Pure — the view's heading. */
function resolveTitle(view, t) {
  if (view === VIEW.BACKUP_CODE) {
    return t('title_backup');
  }
  return t('title');
}

/** Pure — the view's subheading (method-aware for the OTP methods). */
function resolveSubtitle({ view, isOtpMethod, activeMethod, maskedPhone, maskedEmail, t }) {
  if (view === VIEW.BACKUP_CODE) {
    return t('subtitle_backup');
  }
  if (view === VIEW.METHOD_LIST) {
    return t('title_methods');
  }
  if (isOtpMethod) {
    return activeMethod === 'sms'
      ? t('subtitle_sms', { maskedPhone })
      : t('subtitle_email', { maskedEmail });
  }
  if (activeMethod === PASSKEY_METHOD) {
    return t('subtitle_passkey');
  }
  return t('subtitle_totp');
}

/** The header shared by all three views (title + subtitle). Module-private. */
function MfaChallengeHeader({ title, subtitle }) {
  return (
    <div className="mfa-challenge-header">
      <h2 className="mfa-challenge-title">{title}</h2>
      <p className="mfa-challenge-subtitle">{subtitle}</p>
    </div>
  );
}

MfaChallengeHeader.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
};

/**
 * The code-entry view's alt-action row. NOTE: this row's wrapper div is rendered
 * UNCONDITIONALLY (even when both links are hidden) — unlike the backup view's
 * alt-action row, whose wrapper only exists when there are multiple methods.
 * They are deliberately NOT shared: the two markups differ.
 */
function MfaCodeEntryAltActions({
  hasMultipleMethods,
  supportBackupCodes,
  onShowMethods,
  onShowBackup,
  t,
}) {
  return (
    <div className="mfa-alt-actions">
      {hasMultipleMethods && (
        <button
          type="button"
          className="mfa-alt-link"
          onClick={onShowMethods}
          data-testid="mfa-try-another-way"
        >
          {t('link_try_another')}
        </button>
      )}
      {supportBackupCodes && (
        <button
          type="button"
          className="mfa-alt-link"
          onClick={onShowBackup}
          data-testid="mfa-use-backup"
        >
          {t('link_backup')}
        </button>
      )}
    </div>
  );
}

MfaCodeEntryAltActions.propTypes = {
  hasMultipleMethods: PropTypes.bool.isRequired,
  supportBackupCodes: PropTypes.bool.isRequired,
  onShowMethods: PropTypes.func.isRequired,
  onShowBackup: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

/**
 * The code-entry view's answer step for the active method: the passkey ceremony (Q-EE-34 P5), the OTP send-then-enter
 * step for a sent code, or the code field. Module-private.
 *
 * @param {Object} props - the active method, the host's two handlers, and the code field's own props
 * @returns {React.JSX.Element}
 */
function MfaEntryStep({
  activeMethod,
  onSendOtp,
  onVerifyPasskey,
  code,
  onCodeChange,
  onSubmit,
  autoSubmit,
  autoSubmitDelay,
  isLoading,
  error,
  errorId,
  resendCooldown,
  t,
}) {
  if (activeMethod === PASSKEY_METHOD) {
    // Offered only where the host passed `onVerifyPasskey` (`offerableMethods`).
    return (
      <MfaPasskeyStep
        onVerifyPasskey={onVerifyPasskey}
        isLoading={isLoading}
        errorId={errorId}
        t={t}
      />
    );
  }
  if (OTP_METHODS.has(activeMethod) && onSendOtp) {
    return (
      <MfaOtpSendStep
        method={activeMethod}
        onSendOtp={onSendOtp}
        code={code}
        onCodeChange={onCodeChange}
        onSubmit={onSubmit}
        autoSubmit={autoSubmit}
        autoSubmitDelay={autoSubmitDelay}
        isLoading={isLoading}
        error={error}
        errorId={errorId}
        resendCooldown={resendCooldown}
        t={t}
      />
    );
  }
  return (
    <MfaCodeInput
      code={code}
      onChange={onCodeChange}
      onSubmit={onSubmit}
      label={t('label_code')}
      autoSubmit={autoSubmit}
      autoSubmitDelay={autoSubmitDelay}
      focusOnMount
      isLoading={isLoading}
      error={error}
      errorId={errorId}
    />
  );
}

MfaEntryStep.propTypes = {
  activeMethod: PropTypes.string.isRequired,
  onSendOtp: PropTypes.func,
  onVerifyPasskey: PropTypes.func,
  code: PropTypes.string.isRequired,
  onCodeChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  autoSubmit: PropTypes.bool.isRequired,
  autoSubmitDelay: PropTypes.number.isRequired,
  isLoading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  errorId: PropTypes.string,
  resendCooldown: PropTypes.number.isRequired,
  t: PropTypes.func.isRequired,
};

/**
 * @param {Object} props
 * @param {(code: string, method: string) => void|Promise<void>} props.onVerify
 * @param {() => void} props.onCancel
 * @param {string[]} props.methods - the methods the challenge offers, in the server's order (`orderMfaMethods`)
 * @param {string} [props.preferredMethod] - the method the challenge named
 * @param {boolean} [props.supportBackupCodes=false]
 * @param {(method: string) => Promise<void>} [props.onSendOtp]
 * @param {() => Promise<void>} [props.onVerifyPasskey] - runs the passkey ceremony and its verify (Q-EE-34 P5);
 *   without it `passkey` is not offered
 * @param {string} [props.maskedPhone]
 * @param {string} [props.maskedEmail]
 * @param {boolean} [props.isLoading=false]
 * @param {Error|string|null} [props.error=null]
 * @param {boolean} [props.autoSubmit=true]
 * @param {number} [props.autoSubmitDelay=300]
 * @param {number} [props.resendCooldown=60]
 * @param {(key: string, params?: Object) => string} [props.t]
 * @param {string} [props.className]
 * @param {string} [props.cancelLabel]
 * @returns {React.JSX.Element}
 */
export function MfaVerification({
  onVerify,
  onCancel,
  methods: requestedMethods,
  preferredMethod,
  supportBackupCodes = false,
  onSendOtp,
  onVerifyPasskey,
  maskedPhone,
  maskedEmail,
  isLoading = false,
  error = null,
  autoSubmit = true,
  autoSubmitDelay = 300,
  resendCooldown = 60,
  t: externalT,
  className,
  cancelLabel,
}) {
  const methods = offerableMethods(requestedMethods ?? [], onVerifyPasskey);
  // LOGIN-CONVERGENCE: start on the named method when this host can offer it, else on the first it can offer (for the
  // server's ordered list, its own answer once this host has narrowed it). Never a method it was not given, and never
  // an invented one: with nothing to offer there is no method at all, and the view below says why.
  const effectivePreferred = methods.includes(preferredMethod)
    ? preferredMethod
    : (methods[0] ?? null);

  const {
    activeMethod,
    view,
    code,
    setCode,
    handleSelectMethod,
    handleShowMethods,
    handleShowBackup,
    handleBackFromMethods,
  } = useMfaViewState(effectivePreferred);

  useMfaCodeResetOnFailure({ error, isLoading, setCode });

  // Resolution order: the caller's `t` prop → the `mfa` i18next namespace → the English
  // bundle. The middle step is new (2026-08-18): three of the four render sites never
  // passed a `t`, so this dialog rendered English in every locale for them.
  const t = useMfaTranslate(externalT);

  const errorMessage = error instanceof Error ? error.message : error;
  const errorId = errorMessage ? 'mfa-error-message' : undefined;
  const isTerminal = Boolean(error && typeof error === 'object' && error.terminal === true);

  const errorAlert = (
    <MfaErrorAlert
      errorMessage={errorMessage}
      errorId={errorId}
      isTerminal={isTerminal}
      onCancel={onCancel}
      t={t}
    />
  );

  const hasMultipleMethods = methods.length > 1;
  const isOtpMethod = OTP_METHODS.has(activeMethod);

  const handleSubmit = useCallback(() => {
    // Verify button is disabled when isLoading or code is too short, and
    // the MfaCodeInput's auto-submit / Enter handlers gate on the same conditions,
    // so handleSubmit is never invoked with an empty code or while loading.
    onVerify(code, view === VIEW.BACKUP_CODE ? 'backup' : activeMethod);
  }, [code, onVerify, activeMethod, view]);

  const title = resolveTitle(view, t);
  const subtitle = resolveSubtitle({
    view,
    isOtpMethod,
    activeMethod,
    maskedPhone,
    maskedEmail,
    t,
  });

  const rootClassName = `mfa-challenge-form${className ? ` ${className}` : ''}`;
  const backLabel = cancelLabel || t('btn_back');

  if (methods.length === 0) {
    return <MfaNoUsableMethod className={rootClassName} onCancel={onCancel} t={t} />;
  }

  // ─── METHOD LIST VIEW ───
  if (view === VIEW.METHOD_LIST) {
    return (
      <div className={rootClassName} data-testid="mfa-verification">
        <MfaChallengeHeader title={title} subtitle={subtitle} />
        <MfaMethodList
          methods={methods}
          selectedMethod={activeMethod}
          onSelectMethod={handleSelectMethod}
          t={t}
          maskedPhone={maskedPhone}
          maskedEmail={maskedEmail}
        />
        <div className="mfa-challenge-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleBackFromMethods}
            data-testid="mfa-back-btn"
          >
            {backLabel}
          </button>
        </div>
      </div>
    );
  }

  // ─── BACKUP CODE VIEW ───
  if (view === VIEW.BACKUP_CODE) {
    // NOTE: this view's alt-actions wrapper is CONDITIONAL (no div at all when
    // there is a single method), unlike the code-entry view's, which always
    // renders the wrapper. The two are deliberately not shared.
    const backupAltActions = hasMultipleMethods && (
      <div className="mfa-alt-actions">
        <button
          type="button"
          className="mfa-alt-link"
          onClick={handleShowMethods}
          data-testid="mfa-try-another-way"
        >
          {t('link_try_another')}
        </button>
      </div>
    );

    return (
      <div className={rootClassName} data-testid="mfa-verification">
        <MfaChallengeHeader title={title} subtitle={subtitle} />
        <MfaBackupEntry
          code={code}
          onChange={setCode}
          onSubmit={handleSubmit}
          label={t('label_backup')}
          focusOnMount
          isLoading={isLoading}
          error={errorMessage}
          errorId={errorId}
        />
        {errorAlert}
        <div className="mfa-challenge-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleBackFromMethods}
            data-testid="mfa-back-btn"
          >
            {backLabel}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={isLoading || code.length < 8}
            data-testid="mfa-verify-btn"
          >
            {isLoading ? t('btn_verifying') : t('btn_verify')}
          </button>
        </div>
        {backupAltActions}
      </div>
    );
  }

  // ─── CODE ENTRY VIEW (default) ───
  // The Verify button is suppressed when the OTP send-step owns its own verify
  // affordance; `(!isOtpMethod || !onSendOtp)` is preserved verbatim. A passkey has no code
  // to verify: its step's own action starts the ceremony.
  const isPasskeyMethod = activeMethod === PASSKEY_METHOD;
  const verifyButton = !isPasskeyMethod && (!isOtpMethod || !onSendOtp) && (
    <button
      type="button"
      className="btn btn-primary"
      onClick={handleSubmit}
      disabled={isLoading || code.length < 6}
      data-testid="mfa-verify-btn"
    >
      {isLoading ? t('btn_verifying') : t('btn_verify')}
    </button>
  );

  return (
    <div className={rootClassName} data-testid="mfa-verification">
      <MfaChallengeHeader title={title} subtitle={subtitle} />

      <MfaEntryStep
        activeMethod={activeMethod}
        onSendOtp={onSendOtp}
        onVerifyPasskey={onVerifyPasskey}
        code={code}
        onCodeChange={setCode}
        onSubmit={handleSubmit}
        autoSubmit={autoSubmit}
        autoSubmitDelay={autoSubmitDelay}
        isLoading={isLoading}
        error={errorMessage}
        errorId={errorId}
        resendCooldown={resendCooldown}
        t={t}
      />

      {errorAlert}

      <div className="mfa-challenge-actions">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onCancel}
          data-testid="mfa-back-btn"
        >
          {backLabel}
        </button>
        {verifyButton}
      </div>

      <MfaCodeEntryAltActions
        hasMultipleMethods={hasMultipleMethods}
        supportBackupCodes={supportBackupCodes}
        onShowMethods={handleShowMethods}
        onShowBackup={handleShowBackup}
        t={t}
      />
    </div>
  );
}

MfaVerification.propTypes = {
  onVerify: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  methods: PropTypes.arrayOf(PropTypes.string).isRequired,
  preferredMethod: PropTypes.string,
  supportBackupCodes: PropTypes.bool,
  onSendOtp: PropTypes.func,
  onVerifyPasskey: PropTypes.func,
  maskedPhone: PropTypes.string,
  maskedEmail: PropTypes.string,
  isLoading: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Error)]),
  autoSubmit: PropTypes.bool,
  autoSubmitDelay: PropTypes.number,
  resendCooldown: PropTypes.number,
  t: PropTypes.func,
  className: PropTypes.string,
  cancelLabel: PropTypes.string,
};
